import { fetchAvailableTiers } from './tphApi';
import { idleCreditCount } from './onboardingCohort';
import { reportClientFailure } from './clientFailureReport';

/**
 * Story 3.16 — THE SHARED CHECKOUT HANDOFF CHOKE POINT.
 *
 * This module is the SOLE reader of the two ClickFunnels env vars in the
 * client bundle. Before it existed the buy URL was defined THREE times —
 * independently at EditListingDetailsForm.js:69, TierUpgradeModal.js:56 and
 * ListYourHorseLink.js:15 — all falling back to the same literal, with no
 * shared builder and no shared navigation seam. Every door could drift on its
 * own, and one of them (Door 2) sent sellers to checkout with no identity and
 * no return leg at all.
 *
 * ⚠️ DO NOT ADD A FOURTH CONSTANT. `cfHandoff.test.js` asserts, on an
 * ASSERTION and not a grep exit code, that no file outside this one names
 * either env var or the literal host — with exactly two sanctioned exceptions
 * (`server/sellRedirect.js`, which is Door 6 and is recorded as known residue
 * in the story §7, and `render.yaml`, which is where ops sets them).
 *
 * THE TWO DESTINATIONS STAY DISTINCT. Production points both env vars at the
 * same page today (`render.yaml:201-204`), but they are different semantic
 * destinations — the seller LANDING page and the in-app BUY-A-SLOT checkout —
 * and ops must be able to repoint one without the other. Collapsing them into
 * a single export would quietly remove that ability.
 *
 * ---------------------------------------------------------------------------
 * WHY THE AUTHORITATIVE READ MOVED FROM RENDER TIME TO CLICK TIME
 * ---------------------------------------------------------------------------
 * The landing doors (3/4/5) gate on `listYourHorseGoesToWizard`, which decides
 * on `privateData.listingCredits` — documented in `onboardingCohort.js` as "a
 * mirror of the subscriptions truth, not the authority… never a purchase gate."
 * That mirror has a KNOWN DRIFT CLASS (the tier-upgrade route does not convert
 * credits — Story 13.5's `floor_anomaly`). When the mirror reads 0 and the
 * authority would report a slot, those doors send a seller who ALREADY PAID
 * to ClickFunnels having never asked tph-api.
 *
 * The 2026-08-02 audit declined a round-trip at these doors. That was correct
 * for RENDER — every render of every topbar would pay for it — and wrong for
 * the CLICK, which happens once and can afford exactly one round-trip. So the
 * predicate stays where it is (AC1.7: this module COMPOSES it, it does not
 * re-implement it) and the authority is consulted only when a seller actually
 * reaches for checkout.
 */

/**
 * ClickFunnels seller landing page — the "List your horse" destination for
 * logged-out visitors and positively-resolved brand-new users. Falls back to
 * the known-correct production URL so a missed env var degrades to the right
 * page rather than a broken link.
 * @type {string}
 */
export const CF_SELLER_LANDING_URL =
  process.env.REACT_APP_CF_SELLER_LANDING_URL || 'https://list.premierhorse.com';

/**
 * ClickFunnels "buy a listing slot" checkout — the wizard's in-app purchase
 * destination. DISTINCT from the landing page above; see the module note.
 * @type {string}
 */
export const CF_BUY_LISTING_SLOT_URL =
  process.env.REACT_APP_CF_BUY_LISTING_SLOT_URL || 'https://list.premierhorse.com';

/** The two semantic destinations, named so callers never pass a raw URL. */
export const HANDOFF_DESTINATION = Object.freeze({
  SELLER_LANDING: 'seller_landing',
  BUY_LISTING_SLOT: 'buy_listing_slot',
});

/**
 * Resolve a named destination to its URL. Unknown names fall back to the
 * seller landing page — the safe, non-checkout-specific destination.
 * @param {string} destination
 * @returns {string}
 */
export const destinationUrl = destination =>
  destination === HANDOFF_DESTINATION.BUY_LISTING_SLOT
    ? CF_BUY_LISTING_SLOT_URL
    : CF_SELLER_LANDING_URL;

/**
 * AC1.5 — the gate must not present as a hang. Doors show an in-place pending
 * state while it resolves, and after this many ms the AC1.4 failure posture
 * applies as if the endpoint were unavailable.
 *
 * Named (not inlined) so it is adjustable and assertable with fake timers,
 * mirroring `TIER_ZERO_SAVE_DWELL_MS` in EditListingDetailsForm.
 * @type {number}
 */
export const CHECKOUT_HANDOFF_TIMEOUT_MS = 4000;

/** Decisions this gate can return. */
export const HANDOFF_DECISION = Object.freeze({
  PROCEED: 'proceed',
  ASK: 'ask',
  BLOCKED: 'blocked',
});

/**
 * Why the gate decided what it decided. Carried into the AC5 telemetry row and
 * used by the doors to pick which state to render.
 */
export const HANDOFF_REASON = Object.freeze({
  // proceed
  NOT_AUTHENTICATED: 'not_authenticated',
  NO_SLOT: 'no_slot',
  // ⚠️ [[#592]] — the INVERSE disagreement. Recorded, never gated on. See below.
  MIRROR_POSITIVE_AUTHORITY_EMPTY: 'mirror_positive_authority_empty',
  // ask
  ARM_A_MIRROR_ZERO_AUTHORITY_SLOT: 'arm_a_mirror_zero_authority_slot',
  ARM_B_PAID_NOT_SPENDABLE: 'arm_b_paid_not_spendable',
  IDLE_SLOT: 'idle_slot',
  INCONCLUSIVE_MIRROR_POSITIVE: 'inconclusive_mirror_positive',
  // FR-5 — the gate itself threw. Distinct from INCONCLUSIVE_MIRROR_POSITIVE
  // on purpose: that reason means "the authority was unreachable", which is a
  // real observation about tph-api. Recording an unknown internal exception
  // under it would corrupt the count. This should be ~never; if it appears in
  // the AC5 rows at all, something in the gate is broken.
  GATE_THREW: 'gate_threw',
});

const sumSlots = rows => (rows || []).reduce((n, r) => n + (Number(r?.slots) || 0), 0);

const withSlots = rows => (rows || []).filter(r => Number(r?.slots) > 0);

/**
 * Reject after `ms`, so an unreachable-but-not-erroring endpoint still lands on
 * the AC1.4 posture instead of hanging the door forever. Exposed via the
 * `timers` seam below so tests can drive it with fake timers.
 */
const timeoutAfter = ms => {
  let handle;
  const promise = new Promise((_, reject) => {
    handle = setTimeout(() => reject(new Error('CHECKOUT_HANDOFF_TIMEOUT')), ms);
  });
  // N-2 — the loser of the race must be cancellable. Without this every gated
  // click left a live 4s timer behind (harmless in a browser, holds Jest
  // handles open, and gets worse the moment the timeout is raised).
  promise.cancel = () => clearTimeout(handle);
  return promise;
};

/**
 * AC1.3 — the async gate every door funnels through.
 *
 * ```
 * requestCheckoutHandoff({ currentUser, hasAnyOwnListing, destination })
 *   → { decision, idleSlots, tierBreakdown, paidNotSpendable, reason, url }
 * ```
 *
 * `'proceed'` → navigate. `'ask'` → render the modal (AC2) or, for Arm B, the
 * payment-needs-attention state (AC4.2). It resolves the authoritative read via
 * `fetchAvailableTiers()` and FAILS TOWARD `'ask'`, NEVER toward silent
 * checkout, on any inconclusive answer.
 *
 * ---------------------------------------------------------------------------
 * AC4.1 — TWO DISJOINT ARMS, AND THE DIRECTION IS LOAD-BEARING
 * ---------------------------------------------------------------------------
 *   Arm A: the MIRROR says ZERO but the AUTHORITY says SLOT  → ask
 *   Arm B: `paidNotSpendable` (Story 3.17) is NON-EMPTY      → ask
 *
 * 🔴 THE INVERSE IS NOT AN ARM AND MUST NEVER BE FOLDED IN. `listingCredits > 0`
 * while `availableTiers` is EMPTY means the seller has NOTHING to spend, and
 * demanding payment is CORRECT — the mirror is the bug, not the answer. It has
 * two known causes (Story 13.5's tier-upgrade `floor_anomaly`, and the H5
 * credits-applied-row-still-held window) and is ledgered separately. We RECORD
 * it (AC5.6.3) and PROCEED. Making this symmetric would make the choke point
 * refuse to sell to a seller who genuinely needs to buy. RP-4c fences it.
 *
 * ⚠️ AND IT IS NOT THE SAME AS THE ERROR PATH BELOW. On the error path there is
 * NO AUTHORITY ANSWER AT ALL, so the fail-toward-ask posture governs and
 * mirror > 0 means 'ask'. Here the authority ANSWERED, and it said "none".
 * Same variable, opposite readings, opposite outcomes. Do not merge the two
 * branches. RP-6 fences the error leg; RP-4c fences this one.
 *
 * ---------------------------------------------------------------------------
 * ⚠️ WHAT THIS GATE STRUCTURALLY CANNOT SEE — the `held` grant cohort.
 * ---------------------------------------------------------------------------
 * A `held` slot grant has `user_uuid` NULL, no `subscriptions` row, no
 * `listingCredits`, and appears in NEITHER `availableTiers` NOR
 * `paidNotSpendable`. It is unreachable by ANY identity-keyed read, so this
 * gate returns 'proceed' for that seller and MUST NOT claim otherwise. That
 * cohort is carried by the Story 3.15 recovery loop (tokenized claim links,
 * the 6h recovery email, the 2h/48h aged Slack alerts). RP-4d asserts the
 * out-of-scope rather than assuming it.
 *
 * @param {Object} args
 * @param {Object} args.currentUser
 * @param {boolean|null} [args.hasAnyOwnListing]
 * @param {string} [args.destination] one of HANDOFF_DESTINATION
 * @param {Function} [args.fetchTiers] injectable for tests
 * @param {number} [args.timeoutMs]
 * @returns {Promise<Object>}
 */
export const requestCheckoutHandoff = async ({
  currentUser,
  hasAnyOwnListing = null,
  destination = HANDOFF_DESTINATION.SELLER_LANDING,
  fetchTiers = fetchAvailableTiers,
  timeoutMs = CHECKOUT_HANDOFF_TIMEOUT_MS,
} = {}) => {
  const url = destinationUrl(destination);
  const base = {
    idleSlots: 0,
    tierBreakdown: [],
    paidNotSpendable: [],
    url,
    destination,
  };

  // AC1.4 — asymmetric by design. A logged-out visitor has no slot and no
  // identity: this is ad traffic on the paid hot path and must be neither
  // slowed nor blocked. SHORT-CIRCUITS BEFORE ANY FETCH (AC1a.4 / RP-8).
  //
  // 🔴 THIS IS THE ONLY LOGGED-OUT FAST PATH (cold-review B-3). A second one
  // used to exist in the catch below, keyed on the NOT_AUTHENTICATED error
  // code, and it fired for SIGNED-IN sellers whose cookie had expired. Identity
  // is decided HERE, from resolved app state, and nowhere else.
  if (!currentUser?.id) {
    return {
      ...base,
      decision: HANDOFF_DECISION.PROCEED,
      reason: HANDOFF_REASON.NOT_AUTHENTICATED,
    };
  }

  // N-3 — coerce the mirror to a finite number ONCE, here. `idleCreditCount`
  // can return NaN (a `listingCredits: NaN` in privateData), and NaN poisons
  // both directions: `NaN > 0` is false so the fail-toward-ask leg silently
  // stops firing, and `NaN === 0` is false so a genuine Arm A drift case gets
  // recorded under the wrong reason — corrupting the exact count Arm A exists
  // to produce. Not a hypothetical shape: the mirror is written by another
  // service and this module does not own its schema.
  //
  // 🔴 RE-REVIEW FR-6 — `Number.isFinite` alone was not enough. A NEGATIVE or
  // FRACTIONAL mirror walked straight through it: `-1 === 0` is false, so a
  // seller whose credit balance had gone negative (a decrement anomaly — the
  // very drift class Arm A exists to count) was recorded under the generic
  // `idle_slot` reason, which is NOT in `AC4_ARM_REASONS`, so NO SLACK ALERT
  // fired and the Arm A count under-reported. Verbatim the harm the N-3 comment
  // claimed to close. Clamp to a non-negative integer: any balance at or below
  // zero IS "the mirror says nothing", which is exactly Arm A's precondition.
  const rawMirror = idleCreditCount(currentUser);
  const mirrorCredits = Number.isFinite(rawMirror) ? Math.max(0, Math.floor(rawMirror)) : 0;

  let tierBreakdown;
  let idleSlots;
  let paidNotSpendable;
  const timeout = timeoutAfter(timeoutMs);
  try {
    const result = await Promise.race([fetchTiers(), timeout]);

    // ⚠️ COLD-REVIEW SF-5 — THE PARSE LIVES INSIDE THE TRY, DELIBERATELY.
    // These three lines used to sit BELOW the catch, so a 200 whose
    // `availableTiers` was an object or a string (a shape change in another
    // repo, an older/newer tph-api, a proxy returning HTML) threw a TypeError
    // that escaped this function entirely. The caller had already cancelled the
    // click's default navigation, so the seller got NOTHING — no navigation, no
    // modal, no error. "Fail toward ask" has to cover a malformed answer, not
    // just a missing one; a payload we cannot read is exactly as inconclusive
    // as a dead endpoint, and lands on the identical posture below.
    // ⚠️ "THE AUTHORITY ANSWERED NONE" AND "THE AUTHORITY DID NOT ANSWER" ARE
    // DIFFERENT FACTS, and only one of them is a [[#592]] observation.
    // Story 3.17 AC1.5 guarantees `availableTiers` is ALWAYS present and
    // defaults to `[]` — "no consumer should have to tell absent apart from
    // none". So a body WITHOUT the array is a contract violation, not an empty
    // answer, and coercing it to `[]` would (a) proceed on no evidence and
    // (b) write a #592 discriminator row for an event that never happened,
    // polluting the one count that shape exists to produce. Refuse it instead
    // and let the inconclusive posture below govern.
    if (!Array.isArray(result?.availableTiers)) {
      throw new Error('MALFORMED_AUTHORITY_RESPONSE');
    }
    tierBreakdown = withSlots(result.availableTiers);
    idleSlots = sumSlots(tierBreakdown);
    // `paidNotSpendable` is treated more leniently ON PURPOSE: a marketplace
    // deployed ahead of an older tph-api that predates Story 3.17 simply has no
    // Arm B, which is a missing FEATURE rather than a broken answer.
    paidNotSpendable = (Array.isArray(result.paidNotSpendable) ? result.paidNotSpendable : [])
      .filter(r => Number(r?.slots) > 0);
  } catch (e) {
    const code = (e && e.message) || 'TPH_API_UNAVAILABLE';

    // Story 10.19 (D5) — the one code reported from here: tph-api could not be
    // reached or answered unusably, so it may hold no trace of this click. The
    // others are not: NOT_AUTHENTICATED is an expired cookie, handled below;
    // MALFORMED_AUTHORITY_RESPONSE means tph-api answered (it has its own
    // trace); CHECKOUT_HANDOFF_TIMEOUT and TOKEN_REJECTED are out of scope. The
    // reporter is fenced and returns nothing, so the posture below is untouched.
    if (code === 'TPH_API_UNAVAILABLE') {
      reportClientFailure({ surface: 'checkout_handoff', action: 'tier_fetch', error: e });
    }

    // 🔴 COLD-REVIEW B-3 — `NOT_AUTHENTICATED` NO LONGER SHORT-CIRCUITS HERE.
    //
    // The genuinely logged-out visitor was already answered ABOVE, before any
    // fetch (`!currentUser?.id`). Everything reaching this catch is a seller
    // the app has RESOLVED — they have an identity and they may well hold a
    // paid listing. `fetchAvailableTiers` throws NOT_AUTHENTICATED whenever the
    // `st-<clientId>-token` cookie is missing, unparseable or lacks an
    // access_token: cookie cleared, session expired, logged out in another tab,
    // or a clientId/cookie-namespace drift between env and bundle.
    //
    // Treating that as "logged-out ad traffic" sent a signed-in seller holding
    // credits straight to checkout with no ask and no telemetry row — and had
    // the cookie namespace ever drifted it would have done so for EVERY seller
    // at once, silently, with every test still green.
    //
    // ⚠️ THE SPEC WAS WRONG HERE, NOT JUST THE CODE. AC1.4's letter said
    // "NOT_AUTHENTICATED → proceed"; its stated rationale — "a logged-out
    // visitor has no slot and no identity; this is ad traffic" — does not
    // describe this user at all. The AC text is corrected in the story so the
    // next reader does not restore the short-circuit. There is exactly ONE
    // logged-out fast path and it is the pre-fetch one above.
    //
    // So every inconclusive code now shares one posture: the mirror is the only
    // evidence there is, and its drift direction is toward UNDER-reporting,
    // which makes "mirror > 0 with no usable answer" still evidence of a slot.
    if (mirrorCredits > 0) {
      return {
        ...base,
        decision: HANDOFF_DECISION.ASK,
        reason: HANDOFF_REASON.INCONCLUSIVE_MIRROR_POSITIVE,
        // ⚠️ THE COUNT COMES FROM THE MIRROR ON THIS LEG, AND ONLY THIS LEG.
        // Found at the fix round, PRE-EXISTING and not in the cold review:
        // `base.idleSlots` is 0, so every inconclusive ask rendered the AC2
        // screen reading "You've already paid for 0 listings" — a literal
        // falsehood about money, shown today on any TPH_API_UNAVAILABLE or
        // TOKEN_REJECTED with credits, not just on the new paths.
        //
        // The mirror is the ONLY evidence available here and it is precisely
        // what justified asking, so naming it is the honest answer. It is a
        // deliberate, narrow departure from AC2.6's "from the authoritative
        // read" — the authority gave no read at all — and it errs low, since
        // the mirror's drift is toward under-reporting. `tierBreakdown` stays
        // empty because tiers genuinely are unknown here.
        // 🔴 FLAGGED FOR OPERATOR: the alternative is a count-free copy variant,
        // which needs a new approved string. See fix-round FIX-3.
        idleSlots: mirrorCredits,
        error: code,
      };
    }
    return {
      ...base,
      decision: HANDOFF_DECISION.PROCEED,
      // A seller whose mirror also reads zero has no evidence of a slot from
      // any source; proceeding is correct and is NOT the B-3 case.
      reason:
        code === 'NOT_AUTHENTICATED'
          ? HANDOFF_REASON.NOT_AUTHENTICATED
          : HANDOFF_REASON.NO_SLOT,
      error: code,
    };
  } finally {
    timeout.cancel();
  }

  const resolved = { ...base, idleSlots, tierBreakdown, paidNotSpendable };

  // Arm B first: a seller who has PAID but cannot spend needs the billing
  // answer, which is more specific than "use the one you have" — and the two
  // are not mutually exclusive (a seller can hold one spendable slot and one
  // past_due row). Ordering here decides which state renders; Arm B wins
  // because its situation is the one requiring action.
  if (paidNotSpendable.length > 0) {
    return {
      ...resolved,
      decision: HANDOFF_DECISION.ASK,
      reason: HANDOFF_REASON.ARM_B_PAID_NOT_SPENDABLE,
    };
  }

  if (idleSlots > 0) {
    // Arm A is the specific, ledgered shape: the door's render-time mirror said
    // zero and the click-time authority disagreed. When the mirror ALSO says
    // >0 the ask is still correct (the seller does hold a slot) — it just isn't
    // the drift case, so it is recorded under its own reason.
    return {
      ...resolved,
      decision: HANDOFF_DECISION.ASK,
      reason:
        mirrorCredits === 0
          ? HANDOFF_REASON.ARM_A_MIRROR_ZERO_AUTHORITY_SLOT
          : HANDOFF_REASON.IDLE_SLOT,
    };
  }

  // [[#592]] — the authority ANSWERED "none" while the mirror claims credits.
  // Instrument, do NOT gate: this seller genuinely has nothing to spend.
  if (mirrorCredits > 0) {
    return {
      ...resolved,
      decision: HANDOFF_DECISION.PROCEED,
      reason: HANDOFF_REASON.MIRROR_POSITIVE_AUTHORITY_EMPTY,
      mirrorCredits,
    };
  }

  return { ...resolved, decision: HANDOFF_DECISION.PROCEED, reason: HANDOFF_REASON.NO_SLOT };
};

/**
 * AC1a.2 — which clicks the gate is allowed to intercept.
 *
 * Middle-click, ⌘/Ctrl-click, shift-click and anything already handled
 * elsewhere keep TODAY's behaviour: the browser opens the bare href, in a new
 * tab or window, with no gate. The gate is for the plain left-click only.
 * A seller deliberately opening the funnel in a new tab is not a seller we
 * should interrupt.
 * @param {Object} event
 * @returns {boolean}
 */
export const isPlainLeftClick = event =>
  !!event &&
  !event.defaultPrevented &&
  event.button === 0 &&
  !event.metaKey &&
  !event.ctrlKey &&
  !event.shiftKey &&
  !event.altKey;
